/**
 * Deferred monitoring facade.
 *
 * `@revalize/monitoring/frontend` pulls the full Sentry browser SDK, and its
 * `SentryProvider` unconditionally registers `Sentry.replayIntegration()`. Between
 * them that is the single largest contributor to the entry chunk (~950 KB of
 * source, ~300 KB of which is Session Replay) — all of it welded to first paint
 * when imported statically from `main.tsx`.
 *
 * So we load it *after* the first render instead. Two consequences worth knowing:
 *
 *  1. Sentry's global handlers (`window.onerror`, `unhandledrejection`) are
 *     installed a tick later than before, so a throw during the very first paint
 *     may be missed by Sentry. `AppErrorBoundary` still renders the fallback UI,
 *     and any error it catches before Sentry is ready is queued here and flushed
 *     once initialization completes — so boundary-caught errors are never lost.
 *  2. Nothing may import `@revalize/monitoring/frontend` or `@sentry/*`
 *     statically from a module reachable by `main.tsx`, or the deferral is
 *     silently undone. `architecture.test.ts` asserts this.
 */

type MonitoringConfig = Parameters<
  typeof import('@revalize/monitoring/frontend').initializeMonitoring
>[0]

type CaptureFn = typeof import('@revalize/monitoring/frontend').captureException

let capture: CaptureFn | null = null

/** Errors caught before the SDK finished loading, flushed on init. */
const pending: { error: Error; context: Record<string, unknown> | undefined }[] = []

/**
 * Load and initialize monitoring off the critical path.
 *
 * Never rejects: monitoring must not be able to break app startup. Failures are
 * logged and swallowed.
 */
export async function startMonitoring(config: MonitoringConfig): Promise<void> {
  try {
    const mod = await import('@revalize/monitoring/frontend')
    mod.initializeMonitoring(config)
    capture = mod.captureException
    while (pending.length > 0) {
      const queued = pending.shift()
      if (queued) capture(queued.error, queued.context)
    }
  } catch (err) {
    console.error('Monitoring failed to initialize', err) // nosemgrep -- local diagnostic, no user input
  }
}

/**
 * Report an error, queueing it if monitoring has not finished loading yet.
 */
export function reportError(error: Error, context?: Record<string, unknown>): void {
  if (capture) {
    capture(error, context)
    return
  }
  // Bounded so a render loop can't grow this without limit before init resolves.
  if (pending.length < 25) pending.push({ error, context })
}
